const React = require('react');
const RadioButtonGroup = require('components/RadioButtonGroup');
const { css } = require('styled-components');
const RadioButton = require('components/RadioButton');
const DefaultTextField = require('./TextField');
const FieldContext = require('./FieldContext');

const { useMemo, useState } = React;

const internals = {};

internals.getSpecifySentinelValue = ({
    specifyField,
    specifyOptionValue
}) => specifyField.getValue() || specifyOptionValue;

internals.getSelectedValue = ({
    value,
    optionValues,
    specifyOptionValue = ''
}) => {

    if (!value) {
        return '';
    }

    if (optionValues.includes(value)) {
        return value;
    }

    return specifyOptionValue;
};

internals.getValueBuilderResult = ({
    builder,
    fallbackValue,
    params
}) => {

    if (typeof builder !== 'function') {
        return fallbackValue;
    }

    const builtValue = builder(params);
    return typeof builtValue === 'undefined' ? fallbackValue : builtValue;
};

module.exports = function ChoiceWithSpecifyField(props) {

    const {
        field,
        fieldName = field.name,
        options = field.options || [],
        specifyField: specifyFieldConfig = field.specifyField || {},
        specifyFieldName = field.specifyFieldName || specifyFieldConfig.name,
        specifyLabel = field.specifyLabel || specifyField.placeholder || 'Specify',
        specifyOptionValue = field.specifyOptionValue || 'specify',
        textFieldProps = {},
        TextFieldComponent = field.TextFieldComponent || field.textFieldComponent || DefaultTextField,
        useField: requestedUseFieldHook,
        getSelectedValue = field.getSelectedValue || internals.getSelectedValue,
        buildChoiceValue = field.buildChoiceValue,
        buildSpecifyChoiceValue = field.buildSpecifyChoiceValue
    } = props;

    const [pendingSpecifySelected, setPendingSpecifySelected] = useState(false);

    const resolvedUseField = typeof requestedUseFieldHook === 'function'
        ? requestedUseFieldHook
        : typeof field.useField === 'function'
            ? field.useField
            : FieldContext.useField;

    const choiceField = resolvedUseField(fieldName);
    const specifyField = resolvedUseField(specifyFieldName);
    const optionValues = useMemo(() => options.map((option) => option.value), [options]);
    const value = choiceField.getValue() || '';
    const selectedValue = value
        ? getSelectedValue({
            value,
            optionValues,
            specifyOptionValue,
            field,
            choiceField,
            specifyField
        })
        : (pendingSpecifySelected ? specifyOptionValue : '');
    const hasError = choiceField.hasError?.() || false;
    const specifyInputField = {
        ...specifyFieldConfig,
        name: specifyFieldName,
        label: undefined
    };
    const specifyTextFieldProps = {
        ...textFieldProps,
        error: hasError && selectedValue === specifyOptionValue,
        helperText: hasError && selectedValue === specifyOptionValue
            ? (field.specifyRequiredMessage || 'Description required')
            : textFieldProps.helperText,
        placeholder: specifyLabel,
        variant: 'standard',
        inputProps: {
            ...(textFieldProps.inputProps || {}),
            'aria-label': specifyLabel
        },
        InputLabelProps: {
            ...(textFieldProps.InputLabelProps || {})
        },
        onClick: (event) => {
            textFieldProps.onClick?.(event);
            event.stopPropagation();
        },
        fieldStyles: [
            internals.specifyFieldStyles,
            ...(Array.isArray(textFieldProps.fieldStyles)
                ? textFieldProps.fieldStyles
                : textFieldProps.fieldStyles
                    ? [textFieldProps.fieldStyles]
                    : [])
        ]
    };

    return <div
        ref={choiceField.inputRef}
        aria-invalid={hasError || undefined}
    >
        <RadioButtonGroup
            label={field.label}
            labelId={`${field.name}-label`}
            valueSelected={selectedValue}
            onChange={(event) => {

                const selectedChoiceValue = event.target.value;
                const isSpecifyChoice = selectedChoiceValue === specifyOptionValue;
                const specifyValue = specifyField.getValue() || '';
                const fallbackValue = isSpecifyChoice ? specifyValue : selectedChoiceValue;
                const nextValue = internals.getValueBuilderResult({
                    builder: isSpecifyChoice
                        ? buildSpecifyChoiceValue
                        : buildChoiceValue,
                    fallbackValue,
                    params: {
                        event,
                        field,
                        choiceField,
                        specifyField,
                        selectedChoiceValue,
                        specifyOptionValue,
                        value: fallbackValue
                    }
                });

                if (!isSpecifyChoice) {
                    setPendingSpecifySelected(false);
                    specifyField.setValue('', undefined, event);
                }
                else if (!specifyValue) {
                    setPendingSpecifySelected(true);
                }
                else {
                    setPendingSpecifySelected(false);
                }

                choiceField.setValue(nextValue, undefined, event);
            }}
        >
            {options.map((option) => (
                <RadioButton
                    key={option.value}
                    value={option.value}
                    label={option.label}
                />
            ))}
            <RadioButton
                ariaLabel={specifyLabel}
                value={specifyOptionValue}
                label={<TextFieldComponent
                    field={specifyInputField}
                    fieldName={specifyFieldName}
                    value={specifyField.getValue() === specifyOptionValue ? '' : undefined}
                    onChange={(nextValue, event) => {

                        const fallbackValue = nextValue || '';
                        const value = internals.getValueBuilderResult({
                            builder: buildSpecifyChoiceValue,
                            fallbackValue,
                            params: {
                                event,
                                field,
                                choiceField,
                                specifyField,
                                selectedChoiceValue: specifyOptionValue,
                                specifyOptionValue,
                                value: fallbackValue
                            }
                        });

                        setPendingSpecifySelected(!nextValue);
                        choiceField.setValue(value, undefined, event);
                    }}
                    textFieldProps={specifyTextFieldProps}
                    useField={resolvedUseField}
                />}
            />
        </RadioButtonGroup>
    </div>;
};

internals.specifyFieldStyles = css`
    min-height: 32px !important;

    &.MuiFormControl-root,
    div.MuiFormControl-root {
        margin-top: -2px;
        min-height: 32px !important;
    }

    div.MuiInput-root {
        margin-top: 4px !important;
    }

    input.MuiInput-input {
        height: 22px !important;
        line-height: 22px !important;
        padding-top: 2px !important;
        padding-bottom: 3px !important;
    }
`;

module.exports.internals = {
    getSelectedValue: internals.getSelectedValue,
    getSpecifySentinelValue: internals.getSpecifySentinelValue,
    getValueBuilderResult: internals.getValueBuilderResult
};
